import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { fmtDate as _fmtDate, humanizeDates } from '@/lib/dates';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, ChevronRight, Info, AlertCircle, RefreshCw, CalendarDays } from 'lucide-react';

const startOfWeek = (d) => { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; };
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const COMPLETED = ['Completed', 'Work Completed', 'Closed by Admin', 'Closed'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const PR_COLOR = { P1: '#DC2626', P2: '#EA580C', P3: '#D97706', P4: '#2F6DB5', P5: '#64748B' };

// Read-only schedule calendar for Client Facilities Directors.
// Uses the API-scoped /work-orders endpoint, so a director only ever receives
// their own customer's / authorized locations' work orders. Visibility only.
const ClientSchedule = ({ onOpen }) => {
  const { token } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [items, setItems] = useState([]);
  const [mode, setMode] = useState('month');
  const [cursor, setCursor] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setLoading(true); setError(false);
    axios.get(`${API}/work-orders`, authHeaders)
      .then((r) => setItems((r.data || []).filter((w) => (w.scheduled_date || '').slice(0, 10))))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const scheduled = useMemo(() => {
    const map = {};
    items.forEach((w) => { const d = (w.scheduled_date || '').slice(0, 10); if (d) (map[d] = map[d] || []).push(w); });
    return map;
  }, [items]);

  const days = useMemo(() => {
    if (mode === 'week') {
      const s = startOfWeek(cursor);
      return Array.from({ length: 7 }, (_, i) => { const d = new Date(s); d.setDate(s.getDate() + i); return d; });
    }
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d; });
  }, [cursor, mode]);

  const visibleCount = useMemo(() => days.reduce((n, d) => n + (scheduled[iso(d)] || []).length, 0), [days, scheduled]);

  const todayIso = iso(new Date());
  const title = mode === 'week'
    ? `Week of ${_fmtDate(startOfWeek(cursor))}`
    : `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
  const shift = (dir) => { const d = new Date(cursor); if (mode === 'week') d.setDate(d.getDate() + dir * 7); else d.setMonth(d.getMonth() + dir); setCursor(d); };

  return (
    <div data-testid="client-schedule">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8FC93A]">Facilities Director</div>
        <h1 className="text-3xl lg:text-4xl font-extrabold tracking-tight text-[#0E2A47]">Schedule</h1>
        <p className="text-sm text-slate-500 mt-1">Upcoming and completed service across your locations — view only.</p>
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50" data-testid="client-cal-prev"><ChevronLeft size={16} /></button>
          <button onClick={() => setCursor(new Date())} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50" data-testid="client-cal-today">Today</button>
          <button onClick={() => shift(1)} className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50" data-testid="client-cal-next"><ChevronRight size={16} /></button>
          <h3 className="ml-2 text-base font-bold text-[#0E2A47]" data-testid="client-cal-title">{title}</h3>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-3 text-[11px] font-medium text-slate-500">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#2F6DB5]" /> Upcoming</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#5FA718]" /> Completed</span>
          </div>
          <div className="flex gap-1 rounded-lg bg-slate-100 p-0.5">
            <button onClick={() => setMode('month')} data-testid="client-cal-mode-month"
              className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${mode === 'month' ? 'bg-[#0E2A47] text-white shadow-sm' : 'text-slate-600 hover:text-[#0E2A47]'}`}>Month</button>
            <button onClick={() => setMode('week')} data-testid="client-cal-mode-week"
              className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${mode === 'week' ? 'bg-[#0E2A47] text-white shadow-sm' : 'text-slate-600 hover:text-[#0E2A47]'}`}>Week</button>
          </div>
        </div>
      </div>

      {error ? (
        <Card className="border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="py-16 text-center" data-testid="client-schedule-error">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-400"><AlertCircle size={26} /></div>
            <p className="text-sm text-slate-500 mb-4">We couldn't load your schedule.</p>
            <button onClick={load} data-testid="client-schedule-retry" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
              <RefreshCw size={14} /> Try again
            </button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-3">
            {loading ? (
              <div className="h-[480px] animate-pulse rounded-xl bg-slate-100" data-testid="client-schedule-loading" />
            ) : (
              <>
                <div className="grid grid-cols-7 gap-px rounded-xl overflow-hidden border border-slate-200 bg-slate-200">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                    <div key={d} className="bg-slate-50 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-slate-400">{d}</div>
                  ))}
                  {days.map((d, i) => {
                    const key = iso(d);
                    const inMonth = mode === 'week' || d.getMonth() === cursor.getMonth();
                    const list = scheduled[key] || [];
                    const isToday = key === todayIso;
                    return (
                      <div key={i} className={`bg-white p-1.5 ${mode === 'week' ? 'min-h-[240px]' : 'min-h-[108px]'} ${inMonth ? '' : 'bg-slate-50/60'} ${isToday ? 'ring-1 ring-inset ring-[#8FC93A]/50' : ''}`} data-testid={`client-cal-day-${key}`}>
                        <div className={`text-[11px] font-semibold mb-1 ${isToday ? 'text-white bg-[#0E2A47] rounded-full w-5 h-5 flex items-center justify-center' : inMonth ? 'text-slate-600' : 'text-slate-300'}`}>{d.getDate()}</div>
                        <div className="space-y-1">
                          {list.map((w) => {
                            const done = COMPLETED.includes(w.status);
                            const accent = PR_COLOR[w.priority_level] || (done ? '#5FA718' : '#2F6DB5');
                            return (
                              <button key={w.id} onClick={() => onOpen(w.id)} data-testid={`client-cal-wo-${w.tfs_work_order_number}`}
                                title={`${w.tfs_work_order_number} · ${humanizeDates(w.job_name)}${w.priority_level ? ' · ' + w.priority_level : ''} — click to view details`}
                                style={{ borderLeftColor: accent }}
                                className={`group block w-full cursor-pointer text-left rounded-md border-l-[3px] px-1.5 py-1 text-[10px] leading-tight transition-all hover:shadow-md hover:-translate-y-[1px] focus:outline-none focus:ring-2 focus:ring-[#8FC93A] ${done ? 'bg-[#EAF6DD] hover:bg-[#dcefc9]' : 'bg-[#EAF0F8] hover:bg-[#dbe6f5]'}`}>
                                <span className={`font-mono font-semibold ${done ? 'text-[#4f8d14]' : 'text-[#0E2A47]'}`}>{w.tfs_work_order_number}</span>
                                <span className="block truncate text-slate-600 group-hover:text-[#0E2A47]">{humanizeDates(w.job_name)}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {visibleCount === 0 && (
                  <div className="mt-3 flex flex-col items-center justify-center py-6 text-center" data-testid="client-schedule-empty">
                    <CalendarDays size={26} className="text-slate-300 mb-2" />
                    <p className="text-sm text-slate-400">No scheduled service in this {mode === 'week' ? 'week' : 'month'}.</p>
                  </div>
                )}
                <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-400">
                  <Info size={13} /> View-only schedule. Click any item to open the work order details.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientSchedule;
